import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AlertTriangle, Copy, Loader2, Plus, Shield, Trash2 } from "lucide-react";

/**
 * BLOC 19/10 — Super-admin UI codes d'invitation (P0 LLDLS).
 *
 * - Création : champ "entreprise prévue" + TTL (default 7j).
 * - Code en clair affiché UNE SEULE FOIS, non récupérable.
 * - Liste codes avec filtres status (created/used/expired/revoked).
 * - Bouton Révoquer.
 *
 * Protégé backend par require_super_admin (whitelist SUPER_ADMIN_EMAILS).
 * Si 403 → message explicite affiché, pas de crash.
 */
const STATUS_COLORS = {
  created: "bg-blue-900/40 text-blue-200 border-blue-700",
  used: "bg-emerald-900/40 text-emerald-200 border-emerald-700",
  expired: "bg-amber-900/40 text-amber-200 border-amber-700",
  revoked: "bg-slate-700 text-slate-300 border-slate-600",
};

const STATUS_LABEL = {
  created: "Créé",
  used: "Utilisé",
  expired: "Expiré",
  revoked: "Révoqué",
};

export default function SignupInvitesAdminPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [createOpen, setCreateOpen] = useState(false);
  const [newForm, setNewForm] = useState({
    intended_company_name: "",
    intended_email: "",
    ttl_days: 7,
  });
  const [creating, setCreating] = useState(false);
  const [createdInvite, setCreatedInvite] = useState(null); // one-shot clear view

  async function load() {
    setLoading(true);
    try {
      const params = statusFilter !== "all" ? `?status=${statusFilter}` : "";
      const { data } = await api.get(`/admin/signup-invites${params}`);
      setItems(data);
      setError(null);
    } catch (e) {
      if (e?.response?.status === 403) {
        setError("Accès réservé aux super-admins Wérko.");
      } else {
        setError(formatApiError(e));
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  async function create() {
    setCreating(true);
    try {
      const payload = {
        intended_company_name: newForm.intended_company_name.trim(),
        ttl_days: parseInt(newForm.ttl_days, 10) || 7,
      };
      if (newForm.intended_email.trim()) {
        payload.intended_email = newForm.intended_email.trim().toLowerCase();
      }
      const { data } = await api.post("/admin/signup-invites", payload);
      setCreatedInvite(data);
      setCreateOpen(false);
      setNewForm({ intended_company_name: "", intended_email: "", ttl_days: 7 });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCreating(false);
    }
  }

  async function revoke(id) {
    if (!window.confirm("Révoquer définitivement ce code ?")) return;
    try {
      await api.post(`/admin/signup-invites/${id}/revoke`);
      toast.success("Code révoqué");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  const copy = async (txt) => {
    try {
      await navigator.clipboard.writeText(txt);
      toast.success("Copié dans le presse-papier");
    } catch {
      toast.error("Copie manuelle requise");
    }
  };

  if (error) {
    return (
      <div className="p-6" data-testid="signup-invites-error">
        <div className="mx-auto max-w-xl rounded-lg border border-red-500/30 bg-red-500/10 p-6 text-red-200">
          <AlertTriangle className="mb-2 h-6 w-6" />
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="signup-invites-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-100">
            <Shield className="h-6 w-6 text-[#C4553B]" />
            Codes d&apos;invitation
          </h1>
          <p className="text-sm text-slate-400">
            Chaque signup requiert un code à usage unique généré ici.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger
              className="w-40 border-slate-700 bg-slate-900 text-slate-100"
              data-testid="status-filter"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="border-slate-700 bg-slate-900">
              <SelectItem value="all">Tous</SelectItem>
              <SelectItem value="created">Créés</SelectItem>
              <SelectItem value="used">Utilisés</SelectItem>
              <SelectItem value="expired">Expirés</SelectItem>
              <SelectItem value="revoked">Révoqués</SelectItem>
            </SelectContent>
          </Select>
          <Button
            onClick={() => setCreateOpen(true)}
            className="bg-[#C4553B] text-white hover:bg-[#A8402C]"
            data-testid="create-invite-btn"
          >
            <Plus className="mr-2 h-4 w-4" /> Nouveau code
          </Button>
        </div>
      </div>

      <Card className="border-slate-800 bg-slate-900/40">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-10 text-center">
              <Loader2 className="mx-auto h-6 w-6 animate-spin text-slate-400" />
            </div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-400">
              Aucun code pour ce filtre.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-800 text-slate-400">
                  <tr>
                    <th className="p-3 text-left">Code</th>
                    <th className="p-3 text-left">Entreprise prévue</th>
                    <th className="p-3 text-left">Email ciblé</th>
                    <th className="p-3 text-left">Statut</th>
                    <th className="p-3 text-left">Créé</th>
                    <th className="p-3 text-left">Expire</th>
                    <th className="p-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((inv) => (
                    <tr key={inv.id} className="border-b border-slate-800/60 last:border-0">
                      <td className="p-3 font-mono text-slate-300">
                        …{inv.code_last4}
                      </td>
                      <td className="p-3 text-slate-200">{inv.intended_company_name}</td>
                      <td className="p-3 text-slate-400 text-xs">
                        {inv.intended_email || "—"}
                      </td>
                      <td className="p-3">
                        <Badge
                          className={`${STATUS_COLORS[inv.status]} border text-xs`}
                          data-testid={`status-${inv.id}`}
                        >
                          {STATUS_LABEL[inv.status]}
                        </Badge>
                      </td>
                      <td className="p-3 text-xs text-slate-500">
                        {new Date(inv.created_at).toLocaleDateString("fr-BE")}
                      </td>
                      <td className="p-3 text-xs text-slate-500">
                        {new Date(inv.expires_at).toLocaleDateString("fr-BE")}
                      </td>
                      <td className="p-3 text-right">
                        {inv.status === "created" && (
                          <button
                            onClick={() => revoke(inv.id)}
                            className="rounded p-1.5 text-slate-400 hover:bg-red-500/10 hover:text-red-400"
                            data-testid={`revoke-${inv.id}`}
                            aria-label="Révoquer"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="border-slate-800 bg-slate-900 text-slate-100">
          <DialogHeader>
            <DialogTitle>Nouveau code d&apos;invitation</DialogTitle>
            <DialogDescription className="text-slate-400">
              Le code en clair sera affiché UNE SEULE FOIS. Impossible de le récupérer après.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-slate-300">Entreprise prévue *</Label>
              <Input
                value={newForm.intended_company_name}
                onChange={(e) =>
                  setNewForm({ ...newForm, intended_company_name: e.target.value })
                }
                placeholder="LLDLS, ToitPro SPRL, ..."
                className="border-slate-700 bg-slate-950/40"
                data-testid="new-invite-company"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-300">Email destinataire (optionnel)</Label>
              <Input
                type="email"
                value={newForm.intended_email}
                onChange={(e) => setNewForm({ ...newForm, intended_email: e.target.value })}
                placeholder="christophe@lldls.be"
                className="border-slate-700 bg-slate-950/40"
                data-testid="new-invite-email"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-300">Durée de validité (jours)</Label>
              <Input
                type="number"
                min={1}
                max={90}
                value={newForm.ttl_days}
                onChange={(e) => setNewForm({ ...newForm, ttl_days: e.target.value })}
                className="border-slate-700 bg-slate-950/40"
                data-testid="new-invite-ttl"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCreateOpen(false)}
              disabled={creating}
              className="border-slate-700 bg-slate-800 text-slate-200"
            >
              Annuler
            </Button>
            <Button
              onClick={create}
              disabled={creating || !newForm.intended_company_name.trim()}
              className="bg-[#C4553B] text-white hover:bg-[#A8402C]"
              data-testid="confirm-new-invite"
            >
              {creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Créer le code
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* One-shot clear code dialog */}
      <Dialog
        open={createdInvite !== null}
        onOpenChange={(open) => !open && setCreatedInvite(null)}
      >
        <DialogContent
          className="border-amber-700/50 bg-slate-900 text-slate-100"
          data-testid="invite-clear-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-300">
              <AlertTriangle className="h-5 w-5" />
              Code créé — à copier maintenant
            </DialogTitle>
            <DialogDescription className="text-amber-200/80">
              Ce code ne sera plus affiché. Copiez-le ou son lien d&apos;invitation
              maintenant. Si perdu : révoquez et recréez.
            </DialogDescription>
          </DialogHeader>
          {createdInvite && (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-slate-300 text-xs">Code</Label>
                <div className="flex items-center gap-2">
                  <code
                    className="flex-1 rounded border border-slate-700 bg-slate-950 p-3 font-mono text-base tracking-wider text-emerald-300"
                    data-testid="created-invite-code"
                  >
                    {createdInvite.code}
                  </code>
                  <Button
                    size="icon"
                    variant="outline"
                    onClick={() => copy(createdInvite.code)}
                    className="border-slate-700 bg-slate-800"
                    aria-label="Copier le code"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-slate-300 text-xs">Lien d&apos;invitation</Label>
                <div className="flex items-center gap-2">
                  <code
                    className="flex-1 rounded border border-slate-700 bg-slate-950 p-2 font-mono text-xs text-slate-300 break-all"
                    data-testid="created-invite-url"
                  >
                    {createdInvite.invite_url}
                  </code>
                  <Button
                    size="icon"
                    variant="outline"
                    onClick={() => copy(createdInvite.invite_url)}
                    className="border-slate-700 bg-slate-800"
                    aria-label="Copier le lien"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <p className="text-xs text-slate-400">
                Entreprise : <strong>{createdInvite.intended_company_name}</strong> ·
                Valide jusqu&apos;au{" "}
                {new Date(createdInvite.expires_at).toLocaleDateString("fr-BE")}
              </p>
            </div>
          )}
          <DialogFooter>
            <Button
              onClick={() => setCreatedInvite(null)}
              className="bg-[#C4553B] text-white hover:bg-[#A8402C]"
              data-testid="close-clear-dialog"
            >
              J&apos;ai copié, fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
